import Highcharts from 'highcharts';
import { HighchartsReact, HighchartsReactRefObject } from 'highcharts-react-official';
import { useRef, useEffect } from 'react';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import { Box, SxProps, Theme } from '@mui/material';
import { SystemCssProperties } from '@mui/system';
import './Chart.scss';

NoDataToDisplay(Highcharts);

Highcharts.setOptions({
    chart: {
        styledMode: true,
    },
    credits: {
        enabled: false,
    },
    lang: {
        noData: 'NO DATA TO DISPLAY',
    },
});

export type ChartStyleProps = {
    colors: SystemCssProperties['color'][];
    fillColors?: SystemCssProperties['color'][];
    /**
     * Background color for the chart area. Applied to the HighCharts background.
     */
    chartBackgroundColor?: string;
};

type ChartProps = {
    options: Highcharts.Options;
    isLoading: boolean;
    drilldownSupported?: boolean;
    containerSX?: SxProps<Theme>;
} & ChartStyleProps;

function getColorStyles(colors: SystemCssProperties['color'][], fillColors?: SystemCssProperties['color'][]) {
    const styles = colors.reduce<Record<string, any>>((style, color, idx) => {
        style[`.highcharts-color-${idx}`] = {
            fill: fillColors?.[idx] || color,
            stroke: color,
        };

        return style;
    }, {});

    return styles;
}

export function Chart(props: ChartProps) {
    // * disabling dark mode until it's fixed throughout the portal
    // const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const prefersDarkMode = false;

    const chartComponentRef = useRef<HighchartsReactRefObject>(null);
    useEffect(() => {
        if (props.isLoading) {
            chartComponentRef.current?.chart.showLoading();
        } else {
            chartComponentRef.current?.chart.hideLoading();
            chartComponentRef.current?.chart.redraw();
        }
    }, [props.isLoading]);

    const { colors, fillColors, containerSX, chartBackgroundColor } = props;

    return (
        <Box
            sx={[
                (t) => ({
                    width: '100%',
                    minHeight: { xs: 360, sm: 240 },
                    '.highcharts-subtitle': { width: '100%' },
                    '.highcharts-title': { width: '100%' },
                    '.highcharts-loading': {
                        background: 'silver',
                    },
                    '.highcharts-tooltip': {
                        fontSize: t.typography.body1.fontSize,
                    },
                    '.highcharts-loading-inner': {
                        color: 'black',
                    },
                    '.highcharts-area': {
                        'fill-opacity': '1',
                    },
                    '.highcharts-no-data text': {
                        'font-weight': '400',
                        'font-size': '18px',
                    },
                    '.highcharts-point-hover': {
                        cursor: props.drilldownSupported ? 'pointer' : 'default',
                    },
                    // Apply background color to the chart area
                    ...(chartBackgroundColor && {
                        '.highcharts-background': {
                            fill: chartBackgroundColor,
                        },
                    }),
                    ...getColorStyles(colors, fillColors),
                }),
                ...(Array.isArray(containerSX) ? containerSX : [containerSX]),
            ]}
        >
            <HighchartsReact
                containerProps={{
                    className: `highcharts-${prefersDarkMode ? 'dark' : 'light'}`,
                }}
                highcharts={Highcharts}
                options={props.options}
                // @ts-ignore
                ref={chartComponentRef}
            />
        </Box>
    );
}
